import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Link,
  Typography,
} from '@mui/material';
import {
  BugReport as BugReportIcon,
  People as PeopleIcon,
  Description as DocsIcon,
  Code as ReposIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Forum as ChatIcon,
  ConfirmationNumber as JiraIcon,
  Schedule as TimezoneIcon,
  CalendarMonth as CalendarIcon,
  Psychology as ExpertsIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { teamApi, githubIssuesApi, PromptConfig } from '@/services/api';
import ActivityFeed from '@/components/dashboard/ActivityFeed';

export default function TeamOverviewTab() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();

  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamApi.get(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: promptConfig } = useQuery({
    queryKey: ['team-prompt-config', teamId],
    queryFn: () => teamApi.getPromptConfig(teamId!).then((res) => res.data) as Promise<PromptConfig>,
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: issueStats } = useQuery({
    queryKey: ['github-issues-stats', teamId],
    queryFn: () => githubIssuesApi.getStats(teamId!).then((res) => res.data),
    enabled: !!teamId && !!promptConfig?.enable_github_issues,
    staleTime: 60_000,
  });

  const { data: members } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => teamApi.getMembers(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: documentation } = useQuery({
    queryKey: ['team-documentation', teamId],
    queryFn: () => teamApi.getDocumentation(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: repositories } = useQuery({
    queryKey: ['team-repositories', teamId],
    queryFn: () => teamApi.getRepositories(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: experts } = useQuery({
    queryKey: ['team-experts', teamId],
    queryFn: () => teamApi.getExperts(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: responsibilities } = useQuery({
    queryKey: ['team-responsibilities', teamId],
    queryFn: () => teamApi.getResponsibilities(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: providerConfig } = useQuery({
    queryKey: ['team-ai-provider-config', teamId],
    queryFn: () => teamApi.getAIProviderConfig(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  const { data: googleOAuth } = useQuery({
    queryKey: ['team-google-oauth-test', teamId],
    queryFn: () => teamApi.testGoogleOAuth(teamId!).then((res) => res.data),
    enabled: !!teamId,
    staleTime: 60_000,
  });

  if (!team) return null;

  // Calculate KB completeness
  const kbSections = [
    (members?.length || 0) > 0,
    (documentation?.length || 0) > 0,
    (repositories?.length || 0) > 0,
    (experts?.length || 0) > 0,
    (responsibilities?.length || 0) > 0,
  ];
  const kbComplete = kbSections.filter(Boolean).length;
  const kbTotal = kbSections.length;
  const kbPercent = Math.round((kbComplete / kbTotal) * 100);

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Team Info Card */}
        <Grid item xs={12} md={promptConfig?.enable_github_issues ? 7 : 12}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              {/* Description */}
              <Typography variant="body1" sx={{ mb: 2.5, color: team.description ? 'text.primary' : 'text.secondary' }}>
                {team.description || 'No description'}
              </Typography>

              {/* Info row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2.5 }}>
                {/* Google Chat Space */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ChatIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Link
                    href={`https://chat.google.com/app/chat/${team.google_space_id}`}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    variant="body2"
                    sx={{ fontWeight: 500 }}
                  >
                    Google Chat Space
                  </Link>
                </Box>

                {/* JIRA Project */}
                {team.jira_project_key && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <JiraIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    {team.jira_base_url ? (
                      <Link
                        href={`${team.jira_base_url}/projects/${team.jira_project_key}`}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                      >
                        {team.jira_project_key}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{team.jira_project_key}</Typography>
                    )}
                  </Box>
                )}

                {/* Timezone */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <TimezoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{team.timezone}</Typography>
                </Box>

                {/* Created */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {new Date(team.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>

                {/* Bot Persona */}
                {promptConfig?.bot_persona && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <BotIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {promptConfig.bot_persona}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Team Admins */}
              {(() => {
                const admins = (members || []).filter(m => m.role === 'admin' || m.role === 'team_lead');
                if (admins.length === 0) return null;
                const shown = admins.slice(0, 3);
                const remaining = admins.length - shown.length;
                return (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, display: 'block' }}>
                      Team Admins
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                      {shown.map((m) => (
                        <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: m.role === 'admin' ? '#6366F1' : '#8B5CF6' }}>
                            {(m.display_name || m.email)[0].toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {m.display_name || m.email}
                          </Typography>
                          <Chip
                            label={m.role === 'admin' ? 'Admin' : 'Lead'}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, backgroundColor: m.role === 'admin' ? '#EEF2FF' : '#F3E8FF', color: m.role === 'admin' ? '#6366F1' : '#8B5CF6' }}
                          />
                        </Box>
                      ))}
                      {remaining > 0 && (
                        <Link
                          component="button"
                          variant="body2"
                          underline="hover"
                          onClick={() => navigate(`/teams/${teamId}/knowledge?tab=0`)}
                          sx={{ fontWeight: 500 }}
                        >
                          and {remaining} more
                        </Link>
                      )}
                    </Box>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* GitHub Issues Card */}
        {promptConfig?.enable_github_issues && (
          <Grid item xs={12} md={5}>
            <Card
              sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
              onClick={() => navigate(`/teams/${teamId}/issues`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BugReportIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    GitHub Issues
                  </Typography>
                </Box>
                {issueStats ? (
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        {issueStats.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h4" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        {issueStats.open}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Open</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="h4" sx={{ fontWeight: 600, color: '#ed6c02' }}>
                        {issueStats.with_jira}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Linked to JIRA</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No issues synced yet. Click to set up tracking.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Clickable Quick Stats */}
        <Grid item xs={6} md={3}>
          <Card sx={{ '&:hover': { boxShadow: 3 } }}>
            <CardActionArea onClick={() => navigate(`/teams/${teamId}/knowledge?tab=0`)}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {members?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Members</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ '&:hover': { boxShadow: 3 } }}>
            <CardActionArea onClick={() => navigate(`/teams/${teamId}/knowledge?tab=1`)}>
              <CardContent sx={{ textAlign: 'center' }}>
                <DocsIcon sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {documentation?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Documentation</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ '&:hover': { boxShadow: 3 } }}>
            <CardActionArea onClick={() => navigate(`/teams/${teamId}/knowledge?tab=2`)}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ReposIcon sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {repositories?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Repositories</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ '&:hover': { boxShadow: 3 } }}>
            <CardActionArea onClick={() => navigate(`/teams/${teamId}/knowledge?tab=3`)}>
              <CardContent sx={{ textAlign: 'center' }}>
                <ExpertsIcon sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {experts?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Experts</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        {/* Health Status */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Health Status
              </Typography>

              {/* AI Configuration */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, mx: -1, px: 1, py: 0.5, borderRadius: 1 }}
                onClick={() => navigate(`/teams/${teamId}/ai`)}
              >
                <Typography variant="body2">AI Configuration</Typography>
                <Chip
                  icon={providerConfig?.provider_type ? <CheckIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                  label={providerConfig?.provider_type || 'Global defaults'}
                  size="small"
                  color={providerConfig?.provider_type ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>

              {/* Google Chat */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, mx: -1, px: 1, py: 0.5, borderRadius: 1 }}
                onClick={() => navigate(`/teams/${teamId}/settings`)}
              >
                <Typography variant="body2">Google Chat</Typography>
                <Chip
                  icon={googleOAuth?.status === 'success' ? <CheckIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                  label={googleOAuth ? (googleOAuth.status === 'success' ? 'Connected' : 'Not connected') : 'Checking...'}
                  size="small"
                  color={googleOAuth?.status === 'success' ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>

              {/* JIRA Integration */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, mx: -1, px: 1, py: 0.5, borderRadius: 1 }}
                onClick={() => navigate(`/teams/${teamId}/settings`)}
              >
                <Typography variant="body2">JIRA Integration</Typography>
                <Chip
                  icon={team.jira_project_key ? <CheckIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                  label={team.jira_project_key ? 'Connected' : 'Not configured'}
                  size="small"
                  color={team.jira_project_key ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>

              {/* GitHub Issues */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, mx: -1, px: 1, py: 0.5, borderRadius: 1 }}
                onClick={() => navigate(`/teams/${teamId}/settings`)}
              >
                <Typography variant="body2">GitHub Issues</Typography>
                <Chip
                  icon={promptConfig?.enable_github_issues ? <CheckIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                  label={promptConfig?.enable_github_issues ? 'Enabled' : 'Disabled'}
                  size="small"
                  color={promptConfig?.enable_github_issues ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>

              {/* Knowledge Base Completeness */}
              <Box
                sx={{ mt: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, mx: -1, px: 1, py: 0.5, borderRadius: 1 }}
                onClick={() => navigate(`/teams/${teamId}/knowledge`)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2">Knowledge Base</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kbComplete}/{kbTotal} sections
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={kbPercent}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#F3F4F6',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      backgroundColor: kbPercent === 100 ? '#10B981' : kbPercent >= 60 ? '#F59E0B' : '#EF4444',
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Feed */}
        <Grid item xs={12} md={6}>
          <ActivityFeed teamId={teamId} limit={8} title="Recent Activity" />
        </Grid>
      </Grid>
    </Box>
  );
}
